import type { FC } from 'react';
import { ListItem } from '@interstate/components/List';
import { Box } from '@interstate/components/Box';
import { CheckBox } from '@interstate/components/CheckBox';
import { NumericInput } from '@interstate/components/NumericInput';
import type { DisplayItemKey } from '../constants';
import { PAYMENT_RANGE_DEFAULT_SPREAD } from '../constants';

const key = 'paymentRange';

type PaymentRangeValue = {
    finance: number;
    lease: number;
};

type PaymentRangeSectionProps = {
    isActive: boolean;
    value?: string;
    onDisplayActiveChange: (displayKey: DisplayItemKey, checked: boolean) => void;
    onDisplayValueChange: (displayKey: DisplayItemKey, newDisplayValue?: string) => void;
    disabled?: boolean;
};

const parsePaymentRangeValue = (value?: string): PaymentRangeValue => {
    try {
        if (value) {
            const parsed = JSON.parse(value);
            return {
                finance: typeof parsed.finance === 'number' ? parsed.finance : PAYMENT_RANGE_DEFAULT_SPREAD,
                lease: typeof parsed.lease === 'number' ? parsed.lease : PAYMENT_RANGE_DEFAULT_SPREAD
            };
        }
    } catch {
        // fall through to defaults
    }
    return { finance: PAYMENT_RANGE_DEFAULT_SPREAD, lease: PAYMENT_RANGE_DEFAULT_SPREAD };
};

const sanitizeSpreadInput = (input: string): number => {
    const num = parseInt(input, 10);
    if (isNaN(num) || num < 0) {
        return 0;
    }
    return num;
};

const PaymentRangeSection: FC<PaymentRangeSectionProps> = ({
    isActive,
    value,
    onDisplayActiveChange,
    onDisplayValueChange,
    disabled
}) => {
    const parsedValue = parsePaymentRangeValue(value);

    const handleActiveChange = (checked: boolean) => {
        onDisplayActiveChange(key, checked);
        if (checked && !value) {
            onDisplayValueChange(
                key,
                JSON.stringify({ finance: PAYMENT_RANGE_DEFAULT_SPREAD, lease: PAYMENT_RANGE_DEFAULT_SPREAD })
            );
        }
    };

    const handleSpreadChange = (field: 'finance' | 'lease', inputValue: string) => {
        const newSpread = sanitizeSpreadInput(inputValue);
        const updated = { ...parsedValue, [field]: newSpread };
        onDisplayValueChange(key, JSON.stringify(updated));
    };

    return (
        <ListItem>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    minHeight: '64px',
                    justifyContent: 'center',
                    padding: '12px 0'
                }}
            >
                <Box display="flex" alignItems="center" gap="12px">
                    <CheckBox
                        value={key}
                        checked={isActive}
                        onChange={(e) => handleActiveChange(e.target.value.checked)}
                        data-testid={`display-tab-checkbox-${key}`}
                        disabled={disabled}
                    />
                    <span>Payment Range</span>
                </Box>
                {isActive && (
                    <Box display="flex" gap="16px" sx={{ paddingLeft: '36px', marginTop: '8px' }}>
                        <NumericInput
                            sx={{ flex: 1, caretColor: 'auto' }}
                            name="financeSpread"
                            label="Finance Range"
                            inputPrefix="$"
                            value={String(parsedValue.finance)}
                            allowDecimal={false}
                            onChange={(e) => handleSpreadChange('finance', e.target.value)}
                            data-testid="payment-range-finance-spread"
                            disabled={disabled}
                        />
                        <NumericInput
                            sx={{ flex: 1, caretColor: 'auto' }}
                            name="leaseSpread"
                            label="Lease Range"
                            inputPrefix="$"
                            value={String(parsedValue.lease)}
                            allowDecimal={false}
                            onChange={(e) => handleSpreadChange('lease', e.target.value)}
                            data-testid="payment-range-lease-spread"
                            disabled={disabled}
                        />
                    </Box>
                )}
            </Box>
        </ListItem>
    );
};

export default PaymentRangeSection;
